import React, { forwardRef, useEffect, useState, useMemo, ForwardedRef, RefAttributes } from 'react'
import { useActivate, useUnactivate } from 'react-activation'
import type { LoadingContentWrapperProps } from '@jushuitan/ali-react-table'
import { BaseTable, Classes, features, useTablePipeline } from '@jushuitan/ali-react-table'
import { useMemoizedFn } from 'ahooks'
import { Checkbox, Radio, Spin, Tooltip } from 'antd'
import classNames from 'classnames'
import styled from 'styled-components'
import { ContextMenuTrigger } from '@/components/ContextMenu/index'
import { RowInView, RowInViewEventProvider } from './components/RowInView'
import contextMenuHelperBehaviorSubject from './contextMenu/behaviorSubject'
import dragSelectPipeline from './dragSelectPipeline'
import scrollXPipeline from './scrollXPipeline'
import AntEmptyContent from './AntEmptyContent'
import { TableProps, BaseRowData } from './typings'
import styles from './index.less'

const StyledBaseTable = styled(BaseTable)`
    --line-height: 16px;
    --font-size: 12px;
    --row-height: 32px;
    --header-row-height: 36px;
    --cell-padding: 8px;
    --lock-shadow: rgba(0, 0, 0, 0.2) 0 0 10px 0px;
    --border-color: #f0f0f0;
    --color: rgba(0, 0, 0, 0.85);
    --bgcolor: white;
    --hover-bgcolor: #f2f3f5;
    --highlight-bgcolor: #d9e5fc;
    --header-color: rgba(0, 0, 0, 0.85);
    --header-bgcolor: #fafafa;
    --header-hover-bgcolor: #f5f5f5;
    --header-highlight-bgcolor: #f5f5f5;
    .ant-input,
    .ant-input-number,
    .ant-select {
        font-size: 12px;
    }
    &.dark {
        --lock-shadow: black 0 0px 6px 2px;
        --border-color: #303030;
        --color: rgba(255, 255, 255, 0.65);
        --bgcolor: #141414;
        --hover-bgcolor: #262626;
        --highlight-bgcolor: #262626;
        --header-color: rgba(255, 255, 255, 0.85);
        --header-bgcolor: #1d1d1d;
        --hover-hover-bgcolor: #222;
        --header-highlight-bgcolor: #222;
    }
    &.compact {
        --cell-padding: 4px;
    }
    tr:not(.no-highlight).highlight:hover {
        & > td {
            background: var(--hover-bgcolor);
        }
    }
    td {
        overflow-wrap: break-word;
        transition: background 0.3s;
    }
    th {
        font-weight: 400;
        font-size: 13px;
    }
    .${Classes.lockShadowMask} {
        .${Classes.lockShadow} {
            transition: box-shadow 0.3s;
        }
    }
    .art-empty-wrapper {
        pointer-events: auto;
    }
    &.use-outer-border {
        border-top: none;
    }
    &.sticky-header .${Classes.tableHeader} {
        border-top: 1px solid var(--border-color);
    }
    &:not(.bordered) {
        --cell-border-vertical: none;
        --header-cell-border-vertical: none;
        thead > tr.first th {
            border-top: none;
        }
    }
    .art-table-row.first.last {
        .art-table-cell.first.last {
            position: unset !important;
        }
    }
    .resize-handle {
        right: -2px;
        width: 5px;
        -webkit-transition: none;
        transition: none;
    }
    .art-table-header-cell:hover {
        background: #e8ecf3;
    }
    .art-table-header-cell:hover > .resize-handle {
        background: #cdcdcd;
    }
`

const AntLoadingContentWrapper = ({ children, visible }: LoadingContentWrapperProps) => (
    <div className="ant-loading-content-wrapper" style={visible ? { opacity: 0.6 } : {}}>
        {children}
    </div>
)

const BlockSpin = () => <Spin className={styles.blockSpin} />

/** Ant Design 风格的基础表格组件.
 *
 * AntdBaseTable 在 ali-react-table 提供的 BaseTable 基础上定制了默认的表格样式
 * * `className="bordered"` 带边框样式
 * * `className="compact"` 紧凑样式
 * * `className="dark"` 暗色主题
 *
 * 其他样式暂未提供，可以根据需求自行添加~
 *
 * 替换 ant table 组件时的 注意事项
 * 1. dataIndex 变为 code
 * 2. fixed 改为 lock:true
 * 3. 所有的  的  width 必须是 number
 * 4. 手动增加 Pagination 老的 onChange 写在table层和pagination内，参数注意不同
 * 5. 如果有checkBox 使用 useTablePipeline
 * 6. rowSelection 的 selectedRowKeys 要变成 value
 * 7. rowSelection onChange事件 第二个参数不一样selectedRows 可以使用下面的函数
 *    transformSelectedRowKeysToSelectedRows
 *
 *  ant
 *  function(selectedRowKeys, selectedRows)
 *
 *  aliTable
 *  onChange?: (
        nextValue: string[],
        key: string,
        keys: string[],
        action: 'check' | 'uncheck' | 'check-all' | 'uncheck-all',
    ) => void
 *
 * https://ali-react-table.js.org/docs
 *
 * */

const DEFAULT_SELECTION_CONFIG = {
    columnWidth: 40,
    highlightRowWhenSelected: true,
    checkboxPlacement: 'start',
    checkboxColumn: { lock: true },
    clickArea: 'cell',
}

const RowSelectionCheckbox = ({ rowSelection, props }) => {
    const { disabledCheckedTips } = rowSelection
    const [innnerCheck, setCheck] = useState(props.checked)
    useEffect(() => {
        setCheck(props.checked)
    }, [props])

    const onChange = e => {
        setCheck(e.target.checked)
        props?.onChange && props.onChange()
    }

    return React.useMemo(() => {
        if (props.disabled && disabledCheckedTips) {
            return (
                <Tooltip placement="top" title={disabledCheckedTips}>
                    <Checkbox {...props} onChange={onChange} checked={innnerCheck} />
                </Tooltip>
            )
        } else {
            return <Checkbox {...props} onChange={onChange} checked={innnerCheck} />
        }
    }, [props, innnerCheck])
}

const CustomRadio = ({ className, ...restProps }) => {
    return <Radio className={classNames(styles.radio, className)} {...restProps} />
}

const getCtx = ({ rowSelection }) => {
    if (!rowSelection) {
        return
    }

    if (rowSelection.type === 'radio') {
        return { components: { Radio: CustomRadio } }
    }

    return { components: { Checkbox: props => RowSelectionCheckbox({ rowSelection, props }) } }
}

function AntdBaseTable<D extends BaseRowData = BaseRowData>(props: TableProps<D>, ref: ForwardedRef<BaseTable>) {
    const {
        className,
        rowKey,
        primaryKey,
        columns,
        dataSource,
        rowSelection,
        autoRowSpan,
        sort,
        columnResize,
        columnResizeConfig,
        components,
        border = true,
        onChangeSizes,
        onPipeline,
        onDoubleClick,
        rowClickHighlight = false,
        rowOnClick,
        onScrollX, // 滚动状态回调
        defaultClickRowKey,
        contextMenuId,
        dragSelect,
        tempHighlightRowKeys,
        setTempHighlightRowKeys,
        onRowsVisibleChange,
        ...restProps
    } = props

    // 参数
    const ctx = getCtx({ rowSelection })

    const trueRowKey = rowKey || primaryKey || 'key'

    const pipeline = useTablePipeline(ctx).input({ dataSource, columns }).primaryKey(trueRowKey)

    if (dragSelect) {
        pipeline.use(dragSelectPipeline)
    }
    if (onScrollX) {
        // eslint-disable-next-line @typescript-eslint/no-shadow
        pipeline.use(pipeline => {
            scrollXPipeline({ pipeline, onScrollX })
        })
    }

    if (rowSelection) {
        if (rowSelection.type === 'radio') {
            pipeline.use(
                features.singleSelect({
                    ...DEFAULT_SELECTION_CONFIG,
                    ...rowSelection,
                }),
            )
        } else {
            pipeline.use(
                features.multiSelect({
                    ...DEFAULT_SELECTION_CONFIG,
                    ...rowSelection,
                    highlightRowWhenSelected: true,
                    // stopClickEventPropagation: true
                }),
            )
        }
    }

    if (autoRowSpan) {
        pipeline.use(features.autoRowSpan())
    }
    if (columnResize) {
        pipeline.use(
            features.columnResize({
                handleHoverBackground: '#CDCDCD',
                minSize: 60,
                ...columnResizeConfig,
                onChangeSizes(nextSizes) {
                    // 由于列配置不包括选择列，所以需要截取
                    const trueSizes = rowSelection ? nextSizes.slice(1) : nextSizes

                    onChangeSizes?.(trueSizes)
                },
            }),
        )
    }

    if (sort) {
        pipeline.use(features.sort(sort))
    }

    if (typeof onPipeline === 'function') {
        onPipeline(pipeline)
    }

    const pipeProps = pipeline.getProps()

    const [lastClickRowKey, setLastClickRowKey] = useState(() => defaultClickRowKey)

    useEffect(() => {
        if (lastClickRowKey !== defaultClickRowKey) {
            setLastClickRowKey(defaultClickRowKey)
        }
    }, [defaultClickRowKey])

    const getRowProps = (record, rowIndex) => {
        // 注意：这里会有些其他属性，如选中色等，所以需要合并进去
        const innerRowProps =
            typeof pipeProps?.getRowProps === 'function' ? pipeProps.getRowProps(record, rowIndex) : {}

        const onClick = () => {
            setLastClickRowKey(record[trueRowKey])
            // 当默认选中行变化时，清空临时高亮行
            setTempHighlightRowKeys?.([])
            rowOnClick?.(record)

            if (typeof innerRowProps?.onClick === 'function') {
                innerRowProps?.onClick()
            }
        }

        const rowStyle = { ...innerRowProps.style }
        const rowClassNames = (innerRowProps.className || '').split(' ').filter(Boolean)

        if (rowClickHighlight) {
            if (lastClickRowKey === record[trueRowKey]) {
                rowClassNames.push('lastclick')
            } else {
                record._isEdit = false
            }
        }

        if (tempHighlightRowKeys?.includes(record[trueRowKey])) {
            rowClassNames.push('tempHighlight')
        }
        if (restProps?.getRowStyle) {
            Object.assign(rowStyle, restProps?.getRowStyle(record))
        }

        return {
            ...innerRowProps,
            className: rowClassNames.join(' '),
            style: rowStyle,
            onClick,
            onContextMenu: () => {
                if (contextMenuId) {
                    setLastClickRowKey(record[trueRowKey])
                    contextMenuHelperBehaviorSubject.next({
                        record,
                        contextMenuId,
                    })
                }
            },
            onDoubleClick: event => onDoubleClick?.(record, event),
        }
    }

    const onCopy = useMemoizedFn(e => {
        if (!dragSelect) {
            return
        }
        if (!pipeline.getSelection) {
            return
        }
        const value = pipeline.getSelection({ columnOffset: rowSelection ? 1 : 0 })
        if (value) {
            e.clipboardData.setData('text/plain', value)
            e.preventDefault()
        }
    })

    useEffect(() => {
        document.addEventListener('copy', onCopy)
        return () => document.removeEventListener('copy', onCopy)
    }, [])

    useActivate(() => {
        document.addEventListener('copy', onCopy)
    })

    useUnactivate(() => {
        pipeline.clearSelection?.()
        document.removeEventListener('copy', onCopy)
    })
    useEffect(() => {
        if (!onScrollX) {
            return
        }
        // 添加滚动事件监听器
        ref?.current?.domHelper?.tableBody.addEventListener('scroll', pipeline.handleScrollX)
        // 清理函数，确保在组件卸载时移除事件监听器
        return () => {
            ref?.current?.domHelper?.tableBody.removeEventListener('scroll', pipeline.handleScrollX)
        }
    }, [])

    const componentsPropValue = useMemo<TableProps['components']>(() => {
        const propValue: NonNullable<TableProps['components']> = {
            EmptyContent: AntEmptyContent,
            LoadingContentWrapper: AntLoadingContentWrapper,
            LoadingIcon: BlockSpin,
        }

        if (onRowsVisibleChange) {
            propValue.Row = RowInView
        }

        if (components) {
            Object.assign(propValue, components)

            if (components?.Row) {
                console.warn('`onRowsVisibleChange` 与 `components.Row` 属性不能同时使用')
            }
        }

        return propValue
    }, [components, onRowsVisibleChange])

    const baseTable = (
        <RowInViewEventProvider<D> onVisibleChange={onRowsVisibleChange}>
            <StyledBaseTable
                ref={ref}
                {...pipeProps}
                className={classNames(styles.wrap, className, { bordered: border })}
                components={componentsPropValue}
                getRowProps={getRowProps}
                {...restProps}
            />
        </RowInViewEventProvider>
    )

    if (contextMenuId) {
        return <ContextMenuTrigger id={contextMenuId}>{baseTable}</ContextMenuTrigger>
    }

    return baseTable
}

/** 保留泛型参数的 AntdBaseTable 组件类型 */
type AntdBaseTableComponent = <D extends BaseRowData = BaseRowData>(
    props: TableProps<D> & RefAttributes<BaseTable>,
) => React.ReactElement | null

export default React.memo(forwardRef(AntdBaseTable)) as unknown as AntdBaseTableComponent
